import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, TrendingUp, Package } from 'lucide-react';
import logger from '@/utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PackageReports = () => {
  const { getAuthHeaders } = useAdmin();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/reports/packages`, {
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (error) {
      logger.error('Error fetching report:', error);
      toast.error('Failed to fetch package report');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="package-reports">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Package Performance</h1>
        <p className="text-slate-500">Analytics and insights for tour packages</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Packages</p>
                <p className="text-3xl font-bold text-slate-800">{report?.total_packages || 0}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Views</p>
                <p className="text-3xl font-bold text-slate-800">{report?.total_views || 0}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Enquiries</p>
                <p className="text-3xl font-bold text-slate-800">{report?.total_enquiries || 0}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Packages by Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Packages by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {report?.by_category && Object.keys(report.by_category).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(report.by_category).map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="capitalize text-slate-700">{category}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-500 rounded-full"
                          style={{ width: `${(count / report.total_packages) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-600 w-8">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No data available</p>
            )}
          </CardContent>
        </Card>

        {/* Packages by Destination */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Packages by Destination</CardTitle>
          </CardHeader>
          <CardContent>
            {report?.by_destination && Object.keys(report.by_destination).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(report.by_destination)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([destination, count]) => (
                    <div key={destination} className="flex items-center justify-between">
                      <span className="text-slate-700">{destination}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(count / report.total_packages) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-600 w-8">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">No data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Viewed Packages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Top Viewed Packages</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-left text-sm text-slate-600">
                  <th className="p-4 font-semibold">Rank</th>
                  <th className="p-4 font-semibold">Package</th>
                  <th className="p-4 font-semibold">Code</th>
                  <th className="p-4 font-semibold">Destination</th>
                  <th className="p-4 font-semibold">Views</th>
                  <th className="p-4 font-semibold">Enquiries</th>
                  <th className="p-4 font-semibold">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {report?.top_viewed?.map((pkg, index) => {
                  const conversion = pkg.views > 0 
                    ? ((pkg.enquiries / pkg.views) * 100).toFixed(1) 
                    : '0';
                  return (
                    <tr key={pkg.id} className="border-b hover:bg-slate-50">
                      <td className="p-4">
                        <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="p-4 font-medium text-slate-800">{pkg.title}</td>
                      <td className="p-4">
                        <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                          #{pkg.package_code}
                        </span>
                      </td>
                      <td className="p-4 text-slate-600">{pkg.destination}</td>
                      <td className="p-4">
                        <span className="flex items-center gap-1 text-blue-600">
                          <Eye size={14} /> {pkg.views || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="flex items-center gap-1 text-green-600">
                          <TrendingUp size={14} /> {pkg.enquiries || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          parseFloat(conversion) >= 10 ? 'bg-green-100 text-green-700' :
                          parseFloat(conversion) >= 5 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {conversion}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(!report?.top_viewed || report.top_viewed.length === 0) && (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No package data available</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Enquired Packages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Most Enquired Packages</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-left text-sm text-slate-600">
                  <th className="p-4 font-semibold">Rank</th>
                  <th className="p-4 font-semibold">Package</th>
                  <th className="p-4 font-semibold">Code</th>
                  <th className="p-4 font-semibold">Price</th>
                  <th className="p-4 font-semibold">Enquiries</th>
                </tr>
              </thead>
              <tbody>
                {report?.top_enquired?.filter(p => p.enquiries > 0).map((pkg, index) => (
                  <tr key={pkg.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                        index === 0 ? 'bg-green-100 text-green-700' :
                        index === 1 ? 'bg-green-50 text-green-600' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-4 font-medium text-slate-800">{pkg.title}</td>
                    <td className="p-4">
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        #{pkg.package_code}
                      </span>
                    </td>
                    <td className="p-4 text-slate-600">₹{pkg.price?.toLocaleString()}</td>
                    <td className="p-4">
                      <span className="flex items-center gap-1 text-green-600 font-semibold">
                        <TrendingUp size={14} /> {pkg.enquiries}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!report?.top_enquired || report.top_enquired.filter(p => p.enquiries > 0).length === 0) && (
              <div className="text-center py-12 text-slate-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No enquiry data available yet</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PackageReports;
